import { Component, OnInit } from '@angular/core';
import { FeedbackSurveyService } from '../services/survey-tools.service';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { takeUntil, map, tap } from 'rxjs/operators';
import { Subject, forkJoin, Observable, of } from 'rxjs';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { ReviewsService } from '../reviews.service';

@Component({
  selector: 'app-survey-tool',
  templateUrl: './survey-tool.component.html',
  styleUrls: ['./survey-tool.component.scss'],
})
export class SurveyToolComponent implements OnInit {
  private static readonly NON_DATA_COLUMN_KEYS = new Set([
    'responded_to',
    'internal_user_comment',
    'responded_by',
    'record_type',
  ]);
  private ngUnsubscribe$: Subject<any> = new Subject();
  private userLoginId: number;
  private accountId: number;
  private currentAccountIdent: string = '';
  private currentPageNumber: number;
  public currentUser: any;

  public actionItems = [
    {
      label: 'CSV (All Records)',
      command: () => {
        this.exportCSVAllRecords('csv');
      },
      disabled: true,
      visible: false,
    },
    {
      label: 'XLSX (All Records)',
      command: () => {
        this.exportXLSXAllRecords();
      },
      disabled: true,
      visible: false,
    },
    {
      label: 'CSV (Current Page)',
      command: () => {
        this.exportCSVCurrentPage('csv');
      },
      disabled: false,
    },
    {
      label: 'XLSX (Current Page)',
      command: () => {
        this.exportXLSXCurrentPage();
      },
      disabled: false,
    },
  ];
  // Survey rows for the current server page (lazy-loaded)
  public feedbackSurveysList: any[] = [];
  // Main loading state of the page
  public isLoadingData = false;
  public statisticsList: any[] = [];
  public displayColumns: Array<{ key: string; label: string; sortField: string | null }> = [];
  // Paginator properties
  public first: number = 0;
  public rows: number = 25;
  public totalRecords: number = 0;
  private totalPages: number = 1;
  // Dialog properties
  public showEditDialog: boolean = false;
  public selectedSurvey: any = null;
  public editInternalComment: string = '';
  public editRespondedTo: boolean = false;
  public isSavingResponse: boolean = false;
  public isExporting: boolean = false;
  private allRecordsCache: any[] | null = null;

  constructor(
    private feedbackSurveyService: FeedbackSurveyService,
    private sessionService: SessionService,
    private notifyService: NotifyService,
    private reviewsService: ReviewsService
  ) {}

  ngOnInit(): void {
    this.getCurrentAccountDetails();
    this.getCurrentUserDetails();
    // this.getFeedbackSurveysList();
  }

  /**
   * @description Syncs numeric accountId and accountIdent from the selected-account stream.
   * Clears the all-records export cache whenever the selection changes.
   * @returns None.
   */
  private getCurrentAccountDetails(): void {
    this.sessionService
      .getSelectedAccount$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((account) => {
        this.accountId = account?._id || null;
        this.currentAccountIdent = account?.ident || '';
        this.allRecordsCache = null;
        this.totalPages = 1;
      });
  }

  /**
   * @description Loads one survey-results page, formats rows, and builds dynamic column metadata from API keys.
   * Updates totalRecords, totalPages from page_count, summary, and the table row list.
   * @param page Optional one-based page number sent to the survey API. Defaults to API behavior when omitted.
   * @param sortOrder Sorting direction for server-side ordering by date-like fields. Defaults to 'desc'.
   * @returns None. Updates list, statistics, pagination count, and loading state in place.
   * @throws None. API errors are handled in the error callback and surfaced with notifications.
   */
  private getFeedbackSurveysList(page?: number, sortOrder: string = 'desc'): void {
    this.isLoadingData = true;
    this.feedbackSurveyService
      .getFeedbackSurveysList(this.accountId, this.userLoginId, page, sortOrder)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        (res) => {
          console.log('Raw data from getFeedbackSurveysList API:', res);
          const records = res?.records ?? res?.data?.records ?? [];
          this.displayColumns = this.buildDisplayColumns(records);
          if (records.length) {
            console.log('First raw record (keys from API):', records[0], 'Keys:', Object.keys(records[0]));
          }
          this.feedbackSurveysList = this.feedbackSurveyService.formatFeedbackSurveyData(records);
          console.log('Formatted feedbackSurveysList after processing:', this.feedbackSurveysList);
          this.totalRecords = res?.total ?? res?.data?.total ?? 0;
          this.totalPages = res?.page_count ?? res?.data?.page_count ?? 1;
          this.statisticsList = res?.summary ?? res?.data?.summary ?? [];
          this.isLoadingData = false;
          this.notifyService.success('Feedback surveys fetched successfully.');
        },
        (err) => {
          this.isLoadingData = false;
          this.notifyService.info('Error while fetching feedback surveys.');
        },
        () => {
          this.isLoadingData = false;
        }
      );
  }

  /**
   * @description Returns cached all-page rows when allRecordsCache is set; otherwise requests each page in parallel.
   * Page count uses this.totalPages from the last list load (defaults to 1 until the API sets page_count).
   * Populates allRecordsCache only if accountId still matches the id captured when the parallel fetch started.
   * @param sortOrder Sorting direction passed to each page request. Defaults to 'desc'.
   * @returns Observable that emits a single flat array of all formatted survey records.
   * @throws None. Any HTTP errors surface through the subscriber's error handler.
   */
  private fetchAllSurveyRecords(sortOrder: string = 'desc'): Observable<any[]> {
    if (this.allRecordsCache) {
      return of(this.allRecordsCache);
    }
    if (!this.totalPages) {
      return of([]);
    }

    const accountIdAtFetchTime = this.accountId;
    const pageRequests = [];
    for (let i = 0; i < this.totalPages; i++) {
      pageRequests.push(
        this.feedbackSurveyService.getFeedbackSurveysList(this.accountId, this.userLoginId, i + 1, sortOrder)
      );
    }

    return forkJoin(pageRequests).pipe(
      map((responses: any[]) =>
        responses.reduce((acc: any[], res: any) => {
          const records = res?.records ?? res?.data?.records ?? [];
          return acc.concat(this.feedbackSurveyService.formatFeedbackSurveyData(records));
        }, [])
      ),
      tap((records) => {
        if (this.accountId === accountIdAtFetchTime) {  // only set the cache if the account hasn't changed
          this.allRecordsCache = records;
        }
      })
    );
  }

  /**
   * @description Fetches all pages (or cache) then writes a CSV via the reviews service. Sets isExporting for the request lifecycle.
   * @param format Export format identifier passed to the CSV report utility.
   * @returns None.
   * @throws None. Any export or HTTP errors bubble from downstream services.
   */
  exportCSVAllRecords(format: string): void {
    this.isExporting = true;
    const accountIdent = this.currentAccountIdent;
    this.fetchAllSurveyRecords().subscribe({
      next: (records) => {
        this.reviewsService.genCSVReport(records, format, `feedback-survey-all-${accountIdent}`);
      },
      error: () => { this.isExporting = false; },
      complete: () => { this.isExporting = false; },
    });
  }

  /**
   * @description Fetches all pages (or cache) then writes an XLSX; headers come from the first row’s keys.
   * Shows a notification when there are no rows. Sets isExporting for the request lifecycle.
   * @returns None.
   * @throws None. Any export or HTTP errors bubble from downstream services.
   */
  exportXLSXAllRecords(): void {
    this.isExporting = true;
    const accountIdent = this.currentAccountIdent;
    this.fetchAllSurveyRecords().subscribe({
      next: (records) => {
        if (!records.length) {
          this.notifyService.info('No survey records available to export.');
          return;
        }
        const headers = Object.keys(records[0]);
        const tableData = records.map(survey => Object.values(survey));
        const xlslDataArray = [headers, ...tableData];
        this.reviewsService.exportAsExcelFile(xlslDataArray, `feedback-survey-all-${accountIdent}`);
      },
      error: () => { this.isExporting = false; },
      complete: () => { this.isExporting = false; },
    });
  }


  /**
   * @description Exports feedbackSurveysList (current loaded page) as CSV via the reviews service.
   * @param format Export format identifier passed to the CSV report utility.
   * @returns None.
   * @throws None. Any export utility failures bubble from downstream services.
   */
  exportCSVCurrentPage(format: string): void {
    this.reviewsService.genCSVReport(this.feedbackSurveysList, format, 'feedback-survey-page-' + this.currentPageNumber + '-' + this.currentAccountIdent);
  }

  /**
   * @description Exports feedbackSurveysList (current loaded page) as XLSX when rows exist; otherwise notifies only.
   * @returns None.
   * @throws None. Any export utility failures bubble from downstream services.
   */
  exportXLSXCurrentPage(): void {
    if (!this.feedbackSurveysList.length) {
      this.notifyService.info('No survey records available to export.');
      return;
    }

    const headers = Object.keys(this.feedbackSurveysList[0]);
    const tableData = this.feedbackSurveysList.map((survey) => Object.values(survey));
    const xlslDataArray = [headers, ...tableData];
    this.reviewsService.exportAsExcelFile(xlslDataArray, 'feedback-survey-page-' + this.currentPageNumber + '-' + this.currentAccountIdent);
  }

  /**
   * @description Stores paginator first/rows from the PrimeNG page event.
   * @param event Paginator event with first and rows.
   * @returns None.
   */
  public pageChange(event): void {
    this.first = event.first;
    this.rows = event.rows;
  }

  /**
   * @description Handles PrimeNG lazy-load: maps sort meta to asc/desc, sets currentPageNumber from event.first.
   * Reloads one API page via getFeedbackSurveysList (page size 25 must match the table [rows] binding).
   * @param event Lazy-load / sort event from the table.
   * @returns None.
   */
  public lazyLoadLocations(event) {
    let sortOrder: string = 'desc';
    if (!event.multiSortMeta) {
      sortOrder = 'desc';
    } else {
      if (event.multiSortMeta[0].order === 1) {
        sortOrder = 'asc';
      } else if (event.multiSortMeta[0].order === -1) {
        sortOrder = 'desc';
      } else {
        sortOrder = 'desc';
      }
    }
    this.currentPageNumber = event.first / 25 + 1;
    this.getFeedbackSurveysList(this.currentPageNumber, sortOrder);
  }

  /**
   * @description Builds column definitions from raw records: first-seen key order, skips fixed edit-helper keys.
   * Marks date-like columns with a server sortField where supported.
   * @param records Raw survey records returned by the survey results endpoint.
   * @returns Ordered column metadata array. Returns an empty array when records are not present.
   * @throws None.
   */
  private buildDisplayColumns(records: any[]): Array<{ key: string; label: string; sortField: string | null }> {
    if (!Array.isArray(records) || !records.length) {
      return [];
    }

    const orderedKeys = records.reduce((keys: string[], record: Record<string, any>) => {
      Object.keys(record || {}).forEach((key) => {
        if (!SurveyToolComponent.NON_DATA_COLUMN_KEYS.has(key) && !keys.includes(key)) {
          keys.push(key);
        }
      });
      return keys;
    }, []);

    return orderedKeys.map((key) => ({
      key,
      label: this.formatColumnLabel(key),
      sortField: this.getSortFieldForColumn(key),
    }));
  }

  /**
   * @description Turns a raw record key into a readable header (snake/camel split, simple capitalization).
   * @param key Raw server key used in survey records.
   * @returns Readable header label for the table column.
   * @throws None.
   */
  private formatColumnLabel(key: string): string {
    if (!key) {
      return '';
    }

    const expanded = key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .trim();

    return expanded
      .split(/\s+/)
      .map((word) => {
        if (word.toUpperCase() === 'ID') {
          return 'ID';
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  /**
   * @description Maps a column key to a backend orderby field for server-side sort, or null if unsupported.
   * @param key Raw server key used in survey records.
   * @returns Sort field value for PrimeNG sort directives, or null when sorting is unavailable.
   * @throws None.
   */
  private getSortFieldForColumn(key: string): string | null {
    if (!key) {
      return null;
    }

    const normalizedKey = key.toLowerCase();
    if (normalizedKey === 'date' || normalizedKey === 'updated_at' || normalizedKey === 'updatedat') {
      return 'updated_at';
    }

    return null;
  }

  /**
   * @description Formats a dynamic cell for the table: empty → 'N/A', arrays joined, objects JSON-stringified.
   * @param record Survey row object rendered in the table body.
   * @param key Column key resolved from server record schema.
   * @returns Primitive display value suitable for interpolation in table cells.
   * @throws None.
   */
  public getDisplayCellValue(record: Record<string, any>, key: string): string | number | boolean {
    const value = record?.[key];

    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'string' && value.trim() === '') {
      return 'N/A';
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : 'N/A';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  }

  /**
   * @description Subscribes to the current user stream and sets userLoginId plus currentUser for the UI/API.
   * @returns None.
   */
  private getCurrentUserDetails(): void {
    this.sessionService
      .getCurrentUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((user) => {
        this.userLoginId = user?.login?._id;
        this.currentUser = user; // Store user for "Responded by" field
      });
  }

  /**
   * @description Opens the response edit dialog and copies the selected row into local edit state.
   * @param survey Survey row to edit.
   * @returns None.
   */
  public openEditDialog(survey: any): void {
    this.selectedSurvey = survey;
    this.editInternalComment = survey?.internal_user_comment || '';
    this.editRespondedTo = survey?.responded_to ?? false;
    this.showEditDialog = true;
  }

  /**
   * @description Closes the edit dialog and clears selection and draft fields without calling the API.
   * @returns None.
   */
  public closeEditDialog(): void {
    this.showEditDialog = false;
    this.selectedSurvey = null;
    this.editInternalComment = '';
    this.editRespondedTo = false;
  }

  /**
   * @description Builds the responded_by payload { user_id, name, email_id } from session user, or null if missing.
   * @returns Payload object or null when login is unavailable.
   */
  public getRespondedByValue(): { user_id: number; name: string; email_id: string } | null {
    if (!this.currentUser?.login) {
      return null;
    }
    const user_id = this.currentUser.login._id;
    const name = this.currentUser.login.note ?? this.currentUser.login.name ?? '';
    const email_id = this.currentUser.login.loginId ?? this.currentUser.email_id ?? '';
    return { user_id, name, email_id };
  }

  /**
   * @description Human-readable “Responded by” label for the table/dialog from getRespondedByValue().
   * @returns Display string or 'N/A' when the user payload is missing.
   */
  public getRespondedByDisplayText(): string {
    const obj = this.getRespondedByValue();
    if (!obj) {
      return 'N/A';
    }
    return obj.email_id ? `${obj.name} (${obj.email_id})` : obj.name || 'N/A';
  }

  /**
   * @description PATCHes response fields via updateSurveyResponse; account_ident prefers the row, else session.
   * On success updates the row in memory, clears allRecordsCache, closes the dialog, and shows a toast.
   * @returns None. No-op when selection or required row fields are missing.
   */
  public saveResponseFields(): void {
    if (!this.selectedSurvey) {
      return;
    }
    const accountIdent = this.selectedSurvey.account_ident || this.currentAccountIdent;
    const type = this.selectedSurvey.record_type;
    if (!accountIdent || !type || type === 'N/A') {
      this.notifyService.info('Missing account or response type; cannot update.');
      return;
    }
    const respondedBy = this.getRespondedByValue();
    const body = {
      account_ident: accountIdent,
      type,
      updates: {
        responded_to: this.editRespondedTo,
        internal_user_comment: this.editInternalComment,
        ...(respondedBy && { responded_by: respondedBy }),
      },
    };
    console.log('Survey update API request body:', body);
    this.isSavingResponse = true;
    this.feedbackSurveyService
      .updateSurveyResponse(this.accountId, this.userLoginId, body)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        () => {
          this.selectedSurvey.responded_to = this.editRespondedTo;
          this.selectedSurvey.internal_user_comment = this.editInternalComment;
          this.selectedSurvey.responded_by = this.getRespondedByDisplayText();
          this.allRecordsCache = null;
          this.notifyService.success('Response updated successfully.');
          this.closeEditDialog();
        },
        () => {
          this.notifyService.info('Error while updating response.');
        },
        () => {
          this.isSavingResponse = false;
        }
      );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
